import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { DURUM_RENKLERI, TODAY } from '@/lib/constants';

const DURUM_ADIMLARI = ['Araca Bildirildi', 'Yolda', 'Müşteri Alındı', 'Transfer Tamamlandı'];
const DONUS_DURUM_ADIMLARI = ['Onaylandı', 'Araca Bildirildi', 'Yolda', 'Müşteri Alındı', 'Transfer Tamamlandı'];
const TUM_DURUMLAR = ['Yeni Rezervasyon', 'Onaylandı', 'Araç Atandı', 'Transfer Tamamlandı', 'İptal Edildi', 'Ödeme Bekliyor'];

interface Reservation {
  id: number;
  rezervasyon_no: string;
  musteri_adi: string;
  telefon: string;
  gelis_saati: string;
  alinis_saati: string | null;
  ucak_kodu: string;
  alinis_yeri: string;
  birak_yeri: string;
  kisi_sayisi: number;
  arac_tipi: string;
  ucret: number;
  para_birimi: string;
  atanan_arac: string | null;
  atanan_plaka: string | null;
  sofor: string | null;
  durum: string;
  donus_durum: string | null;
  transfer_tipi: string;
  donus_tarihi: string | null;
  donus_saati: string | null;
  donus_alinis: string | null;
  donus_birak: string | null;
  isReturnLeg?: boolean;
}

export default function BugunPage() {
  useAuthGuard();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [durumFilter, setDurumFilter] = useState('');
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const todayLabel = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const todayStr = TODAY;
    
    // Fetch outbound legs
    const { data: outbound, error: err1 } = await supabase
      .from('reservations')
      .select('id,rezervasyon_no,musteri_adi,telefon,gelis_saati,alinis_saati,ucak_kodu,alinis_yeri,birak_yeri,kisi_sayisi,arac_tipi,ucret,para_birimi,atanan_arac,atanan_plaka,sofor,durum,donus_durum,transfer_tipi,donus_tarihi,donus_saati,donus_alinis,donus_birak')
      .eq('gelis_tarihi', todayStr)
      .order('gelis_saati', { ascending: true });
    
    // Fetch return legs (gidiş-dönüş only)
    const { data: returnLegs, error: err2 } = await supabase
      .from('reservations')
      .select('id,rezervasyon_no,musteri_adi,telefon,gelis_saati,alinis_saati,ucak_kodu,alinis_yeri,birak_yeri,kisi_sayisi,arac_tipi,ucret,para_birimi,atanan_arac,atanan_plaka,sofor,durum,donus_durum,transfer_tipi,donus_tarihi,donus_saati,donus_alinis,donus_birak')
      .eq('donus_tarihi', todayStr)
      .eq('transfer_tipi', 'Gidiş-Dönüş')
      .order('donus_saati', { ascending: true });
    
    if (err1 || err2) {
      setError((err1 || err2)?.message || '');
    } else {
      const outboundList = (outbound || []).map((r: Reservation) => ({ ...r, isReturnLeg: false }));
      const returnList = (returnLegs || []).map((r: Reservation) => ({ ...r, isReturnLeg: true }));
      // Merge: outbound first, then return legs; deduplicate by id
      const seen = new Set<number>();
      const merged: Reservation[] = [];
      for (const r of outboundList) { seen.add(r.id); merged.push(r); }
      for (const r of returnList) { if (!seen.has(r.id)) { seen.add(r.id); merged.push(r); } }
      setReservations(merged);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!durumFilter) return reservations;
    return reservations.filter(r => {
      const d = r.isReturnLeg ? (r.donus_durum || r.durum) : r.durum;
      return d === durumFilter;
    });
  }, [reservations, durumFilter]);

  const handleDurumDegis = async (id: number, yeniDurum: string, isReturnLeg?: boolean) => {
    setUpdatingId(id);
    if (isReturnLeg) {
      const { error: err } = await supabase.from('reservations').update({ donus_durum: yeniDurum }).eq('id', id);
      if (!err) setReservations(prev => prev.map(r => r.id === id ? { ...r, donus_durum: yeniDurum } : r));
    } else {
      const { error: err } = await supabase.from('reservations').update({ durum: yeniDurum }).eq('id', id);
      if (!err) setReservations(prev => prev.map(r => r.id === id ? { ...r, durum: yeniDurum } : r));
    }
    setUpdatingId(null);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const idToDelete = deleteConfirm;
    setDeleting(true);
    setDeleteConfirm(null);

    const { data, error } = await supabase.functions.invoke('manage-reservations', {
      body: { action: 'delete', id: idToDelete },
    });

    setDeleting(false);

    if (error || !data?.success) {
      setError('Silinemedi: ' + (error?.message || data?.error || 'Bilinmeyen hata'));
      await fetchData();
      return;
    }
    setReservations(prev => prev.filter(r => r.id !== idToDelete));
  };

  const tamamlandi = reservations.filter(r => {
    const d = r.isReturnLeg ? (r.donus_durum || r.durum) : r.durum;
    return d === 'Transfer Tamamlandı';
  }).length;
  const aktif = reservations.filter(r => {
    const d = r.isReturnLeg ? (r.donus_durum || r.durum) : r.durum;
    return d === 'Araç Atandı' || d === 'Onaylandı' || d === 'Yolda' || d === 'Araca Bildirildi';
  }).length;
  const bekleyen = reservations.filter(r => {
    const d = r.isReturnLeg ? (r.donus_durum || r.durum) : r.durum;
    return d === 'Yeni Rezervasyon' || d === 'Ödeme Bekliyor';
  }).length;

  return (
    <AdminLayout>
      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-vip-dark border border-red-500/20 rounded-xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-400 text-xl"></i>
            </div>
            <h3 className="text-base font-semibold text-white text-center mb-2">Rezervasyonu Sil</h3>
            <p className="text-sm text-gray-400 text-center mb-5">Bu işlem geri alınamaz. Emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-md bg-vip-black border border-vip-gold/10 text-gray-400 text-sm hover:text-white transition-colors whitespace-nowrap">İptal</button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2.5 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/30 transition-colors disabled:opacity-50 whitespace-nowrap">
                {deleting ? 'Siliniyor...' : 'Evet, Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Bugünkü Transferler</h2>
          <p className="text-sm text-gray-400 mt-1">{todayLabel} — Tüm günlük transferler</p>
        </div>
        <a href="/rezervasyonlar/yeni" className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap text-center">
          <i className="ri-add-line mr-1"></i>Yeni Rezervasyon
        </a>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-400"></i>
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={fetchData} className="ml-auto text-xs text-red-400 underline">Tekrar dene</button>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-white">{reservations.length}</p>
          <p className="text-xs text-gray-400">Toplam Transfer</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{tamamlandi}</p>
          <p className="text-xs text-gray-400">Tamamlandı</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-400">{aktif}</p>
          <p className="text-xs text-gray-400">Aktif</p>
        </div>
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{bekleyen}</p>
          <p className="text-xs text-gray-400">Bekleyen</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => setDurumFilter('')} className={`px-3 py-1.5 text-xs rounded-md border transition-all ${!durumFilter ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/30' : 'bg-vip-dark text-gray-400 border-vip-gold/10 hover:text-white'}`}>Tümü</button>
        {TUM_DURUMLAR.map(d => (
          <button key={d} onClick={() => setDurumFilter(d === durumFilter ? '' : d)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-all ${durumFilter === d ? 'bg-vip-gold/15 text-vip-gold border-vip-gold/30' : 'bg-vip-dark text-gray-400 border-vip-gold/10 hover:text-white'}`}>
            {d}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-5 animate-pulse">
              <div className="flex gap-3 mb-4"><div className="w-12 h-12 bg-vip-surface rounded-lg"></div><div className="flex-1 space-y-2"><div className="h-4 bg-vip-surface rounded w-1/3"></div><div className="h-3 bg-vip-surface rounded w-1/4"></div></div></div>
              <div className="space-y-2"><div className="h-3 bg-vip-surface rounded"></div><div className="h-3 bg-vip-surface rounded w-3/4"></div></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map(r => {
            const isReturn = r.isReturnLeg;
            const efektifDurum = isReturn ? (r.donus_durum || r.durum) : r.durum;
            const efektifSure = isReturn ? (r.donus_saati || '').slice(0, 5) : (r.gelis_saati || '').slice(0, 5);
            const efektifAlinis = isReturn ? (r.donus_alinis || r.alinis_yeri) : r.alinis_yeri;
            const efektifBirak = isReturn ? (r.donus_birak || r.birak_yeri) : (r.birak_yeri || 'Yok');
            const adimlar = isReturn ? DONUS_DURUM_ADIMLARI : DURUM_ADIMLARI;

            return (
            <div key={`${r.id}-${isReturn ? 'r' : 'g'}`} className={`bg-vip-dark border rounded-lg p-4 md:p-5 ${isReturn ? 'border-amber-500/20' : 'border-vip-gold/10'}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isReturn ? 'bg-amber-500/10' : 'bg-vip-gold/10'}`}>
                    <i className={`text-lg ${isReturn ? 'ri-arrow-go-back-line text-amber-400' : 'ri-time-line text-vip-gold'}`}></i>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-white">{efektifSure || '--:--'}</p>
                      {isReturn && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded border border-amber-500/20 whitespace-nowrap">Dönüş</span>
                      )}
                    </div>
                    {!isReturn && r.alinis_saati && <p className="text-xs text-vip-gold">Alınış: {String(r.alinis_saati).slice(0, 5)}</p>}
                    <p className="text-xs text-gray-500">{r.ucak_kodu}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {updatingId === r.id ? (
                    <div className="w-5 h-5 border border-vip-gold/40 border-t-vip-gold rounded-full animate-spin"></div>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded-md border cursor-pointer ${DURUM_RENKLERI[efektifDurum] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                      {efektifDurum}
                    </span>
                  )}
                  <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center cursor-pointer">
                    <i className="ri-edit-line text-xs"></i>
                  </a>
                  {!isReturn && (
                    <button onClick={() => setDeleteConfirm(r.id)} className="w-7 h-7 rounded-md hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors flex items-center justify-center">
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-user-line text-gray-500 text-xs"></i>
                  <span className="text-sm text-white font-medium">{r.musteri_adi}</span>
                  <span className="text-xs text-gray-500">{r.telefon}</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-gray-500 text-xs"></i>
                  <span className="text-sm text-gray-300">{efektifAlinis}</span>
                  <i className="ri-arrow-right-line text-vip-gold text-xs"></i>
                  <span className="text-sm text-gray-300">{efektifBirak}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                  <span><i className="ri-group-line mr-1"></i>{r.kisi_sayisi} Kişi</span>
                  <span><i className="ri-car-line mr-1"></i>{r.arac_tipi}</span>
                  <span><i className="ri-money-euro-circle-line mr-1"></i>{r.ucret} {r.para_birimi}</span>
                </div>
                {(r.atanan_arac || r.atanan_plaka || r.sofor) && (
                  <div className="flex flex-wrap gap-3 text-xs text-gray-400">
                    {r.atanan_arac && <span><i className="ri-roadster-line mr-1 text-vip-gold"></i>{r.atanan_arac}</span>}
                    {r.atanan_plaka && <span className="font-mono text-gray-500">{r.atanan_plaka}</span>}
                    {r.sofor && <span><i className="ri-steering-line mr-1 text-vip-gold"></i>{r.sofor}</span>}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 pt-3 border-t border-vip-gold/10">
                {adimlar.map(d => (
                  <button key={d} onClick={() => handleDurumDegis(r.id, d, isReturn)}
                    disabled={updatingId === r.id}
                    className={`px-2.5 py-1.5 text-xs rounded-md border transition-all whitespace-nowrap ${efektifDurum === d ? 'bg-vip-gold/20 text-vip-gold border-vip-gold/40' : 'bg-vip-black text-gray-400 border-vip-gold/10 hover:text-white hover:border-vip-gold/20'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-400">Bugün için transfer bulunamadı.</p>
        </div>
      )}
    </AdminLayout>
  );
}